/**
 * Crew & Capacity. People and their licensed seats come first; agents live in
 * Agent Operations. Handing work over transfers context and lease ownership —
 * never an account.
 */
import { useState } from 'react';
import type { TeamMemberTile } from '@dcc/shared';
import { api, ApiRequestError, idempotencyKey } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import { CrewTile } from '../components/CrewTile';
import { Panel, Stat, StateChip, Unknown } from '../components/Primitives';
import {
  DegradedBanner,
  EmptyState,
  ErrorState,
  LiveBadge,
  SkeletonPanel,
} from '../components/States';
import { duration, relativeTime } from '../lib/format';
import { toneFor } from '../lib/tone';

export function Crew() {
  const projection = useProjection(() => api.crew(), []);
  const portfolio = useProjection(() => api.portfolio(), []);
  const live = useLive(['capacity.updated', 'portfolio.updated'], projection.reload);
  const [handoffTarget, setHandoffTarget] = useState<TeamMemberTile | null>(null);

  if (projection.loading) return <SkeletonPanel rows={5} />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">Team capacity network</div>
          <h1 className="topstrip__title">Crew &amp; capacity</h1>
          <p className="small dim">
            Who is driving, who can take over, and where Claude capacity is going unused.
          </p>
        </div>
        <div className="topstrip__meta">
          <StateChip
            kind="capacity"
            state={view.teamRange.state}
            size="lg"
            label={`team range ${view.teamRange.state.toLowerCase()}`}
          />
          <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}

      <Panel
        title="Capacity balance"
        subtitle="Session range, weekly fuel and seat entitlement are separate layers and can disagree."
        tone={toneFor('severity', view.imbalance.severity)}
      >
        <div className="stack">
          <div className="inline">
            <StateChip kind="severity" state={view.imbalance.severity} />
            <strong>{view.imbalance.headline}</strong>
          </div>
          <p className="small">{view.imbalance.detail}</p>
          {view.imbalance.recommendation && (
            <p className="small dim">{view.imbalance.recommendation}</p>
          )}
          {view.imbalance.recommendedHandoff && (
            <div className="card card--toned" data-tone="success">
              <div className="label">Recommended handoff</div>
              <p className="small" style={{ marginTop: 4 }}>
                <strong>{view.imbalance.recommendedHandoff.fromName}</strong> →{' '}
                <strong>{view.imbalance.recommendedHandoff.toName}</strong> ·{' '}
                {view.imbalance.recommendedHandoff.projectName} /{' '}
                {view.imbalance.recommendedHandoff.workstream}
              </p>
              <ul
                className="tiny dim stack stack--tight"
                style={{ margin: '8px 0 0', paddingLeft: 16 }}
              >
                {view.imbalance.recommendedHandoff.rationale.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <button
                type="button"
                className="btn btn--sm btn--primary"
                style={{ marginTop: 12 }}
                onClick={() =>
                  setHandoffTarget(
                    view.members.find(
                      (m) => m.id === view.imbalance.recommendedHandoff!.toMemberId,
                    ) ?? null,
                  )
                }
              >
                Prepare this handoff
              </button>
            </div>
          )}
        </div>
      </Panel>

      <section aria-labelledby="crew-heading" className="stack">
        <h2 id="crew-heading" className="panel__title">
          Colleagues and seats
        </h2>
        <div className="grid grid--crew">
          {view.members.map((member) => (
            <CrewTile key={member.id} member={member} onHandoff={setHandoffTarget} />
          ))}
        </div>
      </section>

      {handoffTarget && (
        <HandoffForm
          target={handoffTarget}
          projects={(portfolio.data?.projects ?? []).map((p) => ({ id: p.id, name: p.name }))}
          onDone={() => {
            setHandoffTarget(null);
            projection.reload();
          }}
          onCancel={() => setHandoffTarget(null)}
        />
      )}

      <div className="grid grid--2">
        <Panel
          title={`Where capacity went in the last ${view.efficiency.windowHours}h`}
          subtitle="Waiting and rework are capacity spent without a completed journey."
        >
          <div className="grid grid--2">
            <Stat
              label="Human review wait"
              value={view.efficiency.humanReviewWaitMinutes}
              unit="m"
              tone="risk"
            />
            <Stat
              label="Dependency wait"
              value={view.efficiency.ciWaitMinutes}
              unit="m"
              tone="attention"
            />
            <Stat
              label="Agent blocked"
              value={view.efficiency.agentBlockedMinutes}
              unit="m"
              tone="risk"
            />
            <Stat label="Rework" value={view.efficiency.reworkMinutes} unit="m" tone="attention" />
          </div>
          <div className="divider" />
          <Stat
            label="Unproductive burn"
            value={
              view.efficiency.unproductiveBurnPercent === null ? (
                <Unknown />
              ) : (
                `${view.efficiency.unproductiveBurnPercent}%/h`
              )
            }
            note="Capacity consumed while waiting or reworking, not while delivering."
          />
        </Panel>

        <Panel title="Recent handoffs" subtitle="Ownership changes with their context bundle.">
          {view.handoffs.length === 0 ? (
            <EmptyState
              title="No handoffs yet"
              body="Nothing has changed hands between colleagues."
            />
          ) : (
            <div className="rows">
              {view.handoffs.map((handoff) => (
                <div key={handoff.id} className="row" style={{ gridTemplateColumns: '1fr auto' }}>
                  <div className="row__stack row__cell" data-label="Handoff">
                    <span>
                      <strong>{handoff.fromName}</strong> → <strong>{handoff.toName}</strong>
                    </span>
                    <span className="row__sub">
                      {handoff.projectName} · {handoff.workstream} · {handoff.reason}
                    </span>
                  </div>
                  <div
                    className="row__stack row__cell"
                    data-label="When"
                    style={{ alignItems: 'flex-end' }}
                  >
                    <span className="chip" data-tone="neutral">
                      {handoff.status.toLowerCase()}
                    </span>
                    <span className="row__sub">{relativeTime(handoff.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel
        title="Team range detail"
        subtitle="Only authorised, currently usable seats are counted."
      >
        <div className="grid grid--2">
          <div>
            <div className="label">Counted</div>
            <p className="small">
              {view.teamRange.eligibleMembers.map((m) => m.name).join(', ') || 'nobody'}
            </p>
            <p className="small dim">
              {view.teamRange.totalAgentHours === null
                ? 'No usable estimate of remaining agent-hours.'
                : `${duration(view.teamRange.totalAgentHours)} of agent time before the next stop.`}
            </p>
          </div>
          <div>
            <div className="label">Not counted</div>
            <ul className="small stack stack--tight" style={{ margin: 0, paddingLeft: 16 }}>
              {view.teamRange.excluded.map((entry) => (
                <li key={entry.memberId}>
                  <strong>{entry.name}</strong> — {entry.reason}
                </li>
              ))}
              {view.teamRange.excluded.length === 0 && <li>Everyone with a seat is counted.</li>}
            </ul>
          </div>
        </div>
      </Panel>
    </>
  );
}

function HandoffForm({
  target,
  projects,
  onDone,
  onCancel,
}: {
  target: TeamMemberTile;
  projects: { id: string; name: string }[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const [workstream, setWorkstream] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    checks: { name: string; ok: boolean; detail: string }[];
    note: string;
  } | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const response = await api.createHandoff(
        projectId,
        workstream,
        target.id,
        reason,
        idempotencyKey('handoff'),
      );
      setResult({ checks: response.checks, note: response.note });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'The handoff could not be prepared.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={`Hand work over to ${target.name}`}
      subtitle="The receiving colleague works on their own authorised seat and acquires a strictly higher lease epoch."
      tone="active"
      actions={
        <button type="button" className="btn btn--sm btn--quiet" onClick={onCancel}>
          Cancel
        </button>
      }
    >
      {result ? (
        <div className="stack">
          <div className="rows">
            {result.checks.map((check) => (
              <div key={check.name} className="row" style={{ gridTemplateColumns: 'auto 1fr' }}>
                <span className="chip" data-tone={check.ok ? 'success' : 'critical'}>
                  {check.name}
                </span>
                <span className="small row__cell" data-label="Detail">
                  {check.detail}
                </span>
              </div>
            ))}
          </div>
          <p className="small">{result.note}</p>
          <button type="button" className="btn btn--primary btn--sm" onClick={onDone}>
            Done
          </button>
        </div>
      ) : (
        <div className="stack">
          <label className="stack stack--tight">
            <span className="label">Project</span>
            <select
              className="btn"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <label className="stack stack--tight">
            <span className="label">Workstream</span>
            <input
              className="btn"
              style={{ justifyContent: 'flex-start' }}
              value={workstream}
              onChange={(event) => setWorkstream(event.target.value)}
              placeholder="tags-lost-pet"
            />
          </label>
          <label className="stack stack--tight">
            <span className="label">Reason</span>
            <textarea
              className="btn"
              style={{ minHeight: 60, padding: 8, justifyContent: 'flex-start', textAlign: 'left' }}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {error && (
            <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !projectId || workstream.length < 2 || reason.trim().length < 3}
            onClick={() => void submit()}
          >
            Check and prepare the handoff
          </button>
          <p className="tiny dim">
            DCC checks permission, capacity, session, leadership and context before it will prepare
            a handoff. It never proposes sharing an account.
          </p>
        </div>
      )}
    </Panel>
  );
}
